import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Divider,
  Box,
  Alert,
} from '@mui/material';
import {
  ReceiptLong as InvoiceIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Dialog for converting a sale order to invoice
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when conversion is confirmed
 * @param {object} saleOrder - Sale order data
 */
const ConvertToInvoiceDialog = ({ open, onClose, onConfirm, saleOrder }) => {
  const { t } = useTranslation();
  const [converting, setConverting] = useState(false);

  const handleConfirm = async () => {
    setConverting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error converting to invoice:', error);
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={converting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 700,
          fontSize: '1.1rem',
          color: '#1976d2',
          pb: 1.5,
        }}
      >
        <InvoiceIcon />
        {t('convert_to_invoice')}
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('convert_to_invoice_info')}
          </Typography>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>{t('sale_order_number')}:</strong> {saleOrder?.SaleOrderNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>{t('customer')}:</strong> {saleOrder?.Customer?.CompanyName || saleOrder?.Customer?.Name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('total_amount')}:</strong> €{saleOrder?.TotalIncl?.toFixed(2) || '0.00'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>
          {t('convert_to_invoice_confirmation')}
        </Typography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={converting}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={converting}
          startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <InvoiceIcon />}
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0',
            },
          }}
        >
          {converting ? t('converting') : t('convert_to_invoice')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToInvoiceDialog;
